import React from 'react';
import { Eye, Play, Copy, Edit, Eye as EyeOff, Lock, RotateCcw, AlertCircle, Upload } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import { cn } from '../../../../../shared/utils';
import { LessonDTO } from '../../../core/types/lesson.types';
import { Role } from '../../../core/types';
import { Markdown } from '@/shared/components/markdown/Markdown';
import {
  configDepunere,
  provocareDejaDepusa,
  rezumatProvocare,
  stareDepuneriText,
  stareProvocareText,
  termenExpirat,
  usesPracticalActions,
} from '../../practical/practical-evidence';
import { ButonDescarcaExercitii } from '../../../components/ButonDescarcaExercitii';
import { ButonDescarcaRaspunsuri } from '../../../components/ButonDescarcaRaspunsuri';

interface LessonCardProps {
  lesson: LessonDTO;
  userRole: Role;

  // Action handlers
  onEdit?: (lessonId: string) => void;
  onComplete?: (lessonId: string) => void;
  onPreview?: (lessonId: string) => void;
  onDuplicate?: (lessonId: string) => void;
  onToggleVisibility?: (lessonId: string, currentlyHidden: boolean) => void;
  onDelete?: (lessonId: string) => void;
  /** B3: pagina „Detalii" (activitati practice: profesor si ADMIN/DBEST). */
  onDetails?: (lessonId: string) => void;
  /** B3: „Încarcă poze" (doar PROFESOR, activitati practice). */
  onUpload?: (lessonId: string) => void;

  // Permissions
  canEdit?: boolean;
  canComplete?: boolean;
  canToggleVisibility?: boolean;
  canDelete?: boolean;

  // Loading states
  isLoading?: boolean;
}

/**
 * Helper: Get button text and state based on attemptState
 * Follows the business logic:
 * - null: Not started
 * - IN_PROGRESS: Working on it
 * - PENDING_APPROVAL: Submitted, awaiting review (disabled)
 * - APPROVED: Professor approved (practice mode)
 * - REJECTED: Professor rejected (allow new scored attempt)
 * - COMPLETED: Auto-approved (practice mode)
 */
export function getButtonState(
  attemptState: string | null | undefined
): {
  label: string;
  variant: 'default' | 'outline';
  icon: React.ReactNode;
  disabled: boolean;
  isPractice: boolean;
} {
  // B3 (24.09): ramura PRACTICAL_ACTIVITY („Trimite altă activitate") a fost scoasa. Singurul
  // apelant care ajungea la ea era cardul PROFESORULUI pe activitati practice, iar acesta nu mai
  // trece pe aici: are „Încarcă poze" + „Detalii" (usesPracticalActions). ELEV nu vede lista de
  // activitati practice (ALLOWED_ROLES: DBEST/ADMIN/PROFESOR), iar blocul ADMIN nu cheama functia.
  // Retrimiterea oricand se face acum din „Încarcă poze".

  switch (attemptState) {
    case 'IN_PROGRESS':
      return {
        label: 'Continuare',
        variant: 'default',
        icon: <Play className="w-4 h-4 mr-2" />,
        disabled: false,
        isPractice: false,
      };
    case 'PENDING_APPROVAL':
      return {
        label: 'În Așteptare Aprobare',
        variant: 'default',
        icon: <Lock className="w-4 h-4 mr-2" />,
        disabled: true,
        isPractice: false,
      };
    case 'APPROVED':
    case 'COMPLETED':
      return {
        label: 'Repornire (Nepunctată)',
        variant: 'outline',
        icon: <RotateCcw className="w-4 h-4 mr-2" />,
        disabled: false,
        isPractice: true,
      };
    case 'REJECTED':
      return {
        label: 'Reîncearcă',
        variant: 'default',
        icon: <AlertCircle className="w-4 h-4 mr-2" />,
        disabled: false,
        isPractice: false,
      };
    case null:
    case undefined:
    default:
      return {
        label: 'Pornire',
        variant: 'default',
        icon: <Play className="w-4 h-4 mr-2" />,
        disabled: false,
        isPractice: false,
      };
  }
}

/**
 * Card component for displaying a lesson in the listing page
 * Similar design to the existing QuizBox but with TypeScript and improved structure
 *
 * @example
 * <LessonCard
 *   lesson={lesson}
 *   userRole={Role.ADMIN}
 *   onEdit={editLesson}
 *   onPreview={previewLesson}
 *   canEdit={true}
 * />
 */
export const LessonCard: React.FC<LessonCardProps> = ({
  lesson,
  userRole,
  onEdit,
  onComplete,
  onPreview,
  onDuplicate,
  onToggleVisibility,
  onDetails,
  onUpload,
  canEdit = false,
  canComplete = false,
  canToggleVisibility = false,
  isLoading = false,
}) => {
  const isAdmin = [Role.ADMIN, Role.DBEST].includes(userRole);
  const isHidden = lesson.isHidden === true;
  const attemptState = (lesson as any).attemptState as string | null | undefined;
  const hasPdfManual = !!lesson.pdfManualUrl;
  const isPractical = lesson.type === 'PRACTICAL_ACTIVITY';
  // Fisele „Exercitii cu clasa”: „Descarca intrebarile” + „Descarca raspunsurile” (fara diacritice, cerinta owner-ului).
  const isClassExercise = lesson.type === 'CLASS_EXERCISE';
  // B3: profesorul pe activitate practica vede „Încarcă poze" + „Detalii", nu parcursul de lectie.
  const practicalActions = usesPracticalActions(lesson.type, userRole);
  // Provocarea pentru scoala (2026-10-02): aceleasi doua butoane, dar o singura depunere per scoala,
  // cu punctaj si termen afisate pe card.
  const isSchoolChallenge = lesson.type === 'SCHOOL_CHALLENGE';
  const stareDepuneri = !practicalActions
    ? null
    : isSchoolChallenge
      ? stareProvocareText(lesson.depuneriInAsteptare, lesson.depuneriAprobate)
      : stareDepuneriText(lesson.depuneriInAsteptare, lesson.depuneriAprobate);
  const rezumat = isSchoolChallenge ? rezumatProvocare(lesson.punctajMaxim, lesson.deadline) : null;
  // „Încarcă" dispare cand nu mai are rost: scoala a trimis deja, sau termenul a trecut.
  const poateIncarca =
    !isSchoolChallenge ||
    (!provocareDejaDepusa(lesson.depuneriInAsteptare, lesson.depuneriAprobate) && !termenExpirat(lesson.deadline));
  const etichetaIncarca = configDepunere(lesson.type)?.texte.butonIncarca ?? 'Încarcă poze';


  // Don't show hidden lessons to non-admin users
  if (isHidden && !isAdmin) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative flex w-full flex-col rounded-lg transition-shadow',
        'bg-white border border-black',
        isHidden && isAdmin && 'lesson-card-hidden'
      )}
    >
        {/* Lesson Image */}
        <img
          className="flex max-h-[220px] min-h-[220px] w-full rounded-t-lg object-cover lesson-image-lazy"
          src={lesson.image || '/quiz-bg.jpg'}
          alt={lesson.title || 'Lesson image'}
          loading="lazy"
        />

        {/* Card Content */}
        <div className="flex h-full flex-1 flex-col justify-around p-5">
          {/* Title */}
          <div className="flex">
            <h5 className="mb-2 text-lg font-bold tracking-tight text-foreground sm:line-clamp-3 sm:max-h-[96px] sm:min-h-[96px] md:text-lg xl:text-2xl">
              {lesson.title}
            </h5>
          </div>

          {/* Description and Actions */}
          <div className="flex h-full flex-1 flex-col">
            {/* B3: descrierea e markdown inline (bold/cursiv/linkuri), taiata la 2 randuri. Inaltimea
                rezervata ramane fixa (2 randuri x 24px), ca grila de carduri sa nu sara. */}
            <div className="mb-3 line-clamp-2 h-full max-h-[48px] min-h-[48px] font-normal text-foreground">
              <Markdown mode="inline" source={lesson.description} />
            </div>

            {rezumat && (
              <p data-provocare-rezumat className="mb-3 text-sm font-semibold text-primary">{rezumat}</p>
            )}

            {stareDepuneri && (
              <p className="mb-3 text-sm text-muted-foreground">{stareDepuneri}</p>
            )}

            {/* Action Buttons */}
            <div className="flex h-max w-full flex-col justify-end self-end gap-1">
              {/* Admin/DBEST Actions */}
              {isAdmin && canEdit && (
                <>
                  <Button
                    variant="link"
                    className="w-full sm:w-auto justify-start"
                    onClick={() => onEdit?.(lesson.id!)}
                    disabled={isLoading}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    <span className="text-wrap">Editare</span>
                  </Button>

                  {canToggleVisibility && onToggleVisibility && (
                    <Button
                      variant="link"
                      className="w-full sm:w-auto justify-start"
                      onClick={() => onToggleVisibility(lesson.id!, isHidden)}
                      disabled={isLoading}
                    >
                      {isHidden ? (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          <span className="text-wrap">Afișare</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" />
                          <span className="text-wrap">Ascundere</span>
                        </>
                      )}
                    </Button>
                  )}

                  {onDuplicate && (
                    <Button
                      variant="link"
                      className="w-full sm:w-auto justify-start"
                      onClick={() => onDuplicate(lesson.id!)}
                      disabled={isLoading}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      <span className="text-wrap">Duplicare</span>
                    </Button>
                  )}

                  {onPreview && (
                    <Button
                      variant="link"
                      className="w-full sm:w-auto justify-start"
                      onClick={() => onPreview(lesson.id!)}
                      disabled={isLoading}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      <span className="text-wrap">Previzualizare</span>
                    </Button>
                  )}

                  {/* B3: „Detalii" pe activitatile practice si pentru ADMIN/DBEST (fara „Încarcă poze"). */}
                  {(isPractical || isSchoolChallenge) && onDetails && (
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => onDetails(lesson.id!)}
                      disabled={isLoading}
                    >
                      Detalii
                    </Button>
                  )}

                  {/* Open PDF Exercises Button - Admin/DBEST */}
                  {/* Task 15: descarcare reala prin backend; un PDF din afara containerului nostru se
                      deschide sincron in tab nou; un esec dupa cerere arata linkul „Deschide PDF-ul". */}
                  {hasPdfManual && lesson.id && (
                    <ButonDescarcaExercitii
                      lessonId={lesson.id}
                      titlu={lesson.title ?? ''}
                      pdfManualUrl={lesson.pdfManualUrl!}
                      className="w-full sm:w-auto"
                      disabled={isLoading}
                      eticheta={isClassExercise ? 'Descarca intrebarile' : undefined}
                    />
                  )}
                  {/* Fisa cu raspunsurile bifate (cheia temei online): doar prin backend. */}
                  {isClassExercise && lesson.areRaspunsuriPdf && lesson.id && (
                    <ButonDescarcaRaspunsuri
                      lessonId={lesson.id}
                      titlu={lesson.title ?? ''}
                      className="w-full sm:w-auto"
                      disabled={isLoading}
                    />
                  )}

                  {/*{canDelete && onDelete && (*/}
                  {/*  <Button*/}
                  {/*    variant="link"*/}
                  {/*    className="w-full sm:w-auto justify-start text-destructive hover:text-red-800"*/}
                  {/*    onClick={() => onDelete(lesson.id!)}*/}
                  {/*    disabled={isLoading}*/}
                  {/*  >*/}
                  {/*    <Trash2 className="w-4 h-4 mr-2" />*/}
                  {/*    <span className="text-wrap">Ștergere</span>*/}
                  {/*  </Button>*/}
                  {/*)}*/}
                </>
              )}

              {/* Professor Actions */}
              {userRole === Role.PROFESOR && !isAdmin && (
                <>
                  {/* B3: activitate practica -> „Încarcă poze" (plin) + „Detalii" (contur), pe un rand.
                      Nu trece prin getButtonState si nu are „Previzualizare". */}
                  {/* Final review I1: cu doua `flex-1`, la 360 fiecare buton avea ~130px si „Încarcă poze"
                      se rupea pe doua randuri. Primarul ia tot spatiul ramas, „Detalii" doar cat textul lui,
                      iar ambele etichete raman pe un rand (`whitespace-nowrap`). */}
                  {practicalActions && !isHidden && (
                    <div className="flex gap-2">
                      {poateIncarca && (
                        <Button
                          className="min-w-0 flex-1"
                          onClick={() => onUpload?.(lesson.id!)}
                          disabled={isLoading || !onUpload}
                        >
                          <Upload className="w-4 h-4" aria-hidden="true" />
                          <span className="whitespace-nowrap">{etichetaIncarca}</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className={poateIncarca ? 'shrink-0 px-4' : 'flex-1'}
                        onClick={() => onDetails?.(lesson.id!)}
                        disabled={isLoading || !onDetails}
                      >
                        <span className="whitespace-nowrap">Detalii</span>
                      </Button>
                    </div>
                  )}

                  {/* Completion button - for CLASS_EXERCISE and other completable lessons */}
                  {!practicalActions && canComplete && onComplete && !isHidden && (() => {
                    const buttonState = getButtonState(attemptState);
                    return (
                      <Button
                        variant={buttonState.variant}
                        className={cn(
                          'w-full',
                          buttonState.variant === 'default' && 'bg-primary hover:bg-blue-700 text-white',
                          buttonState.disabled && 'opacity-50 cursor-not-allowed'
                        )}
                        onClick={() => onComplete(lesson.id!)}
                        disabled={isLoading || buttonState.disabled}
                        title={
                          buttonState.isPractice
                            ? 'Mod exersare - răspunsurile nu vor fi salvate'
                            : buttonState.disabled
                            ? 'Așteptați aprobarea administratorului'
                            : undefined
                        }
                      >
                        {buttonState.icon}
                        <span className="text-wrap">{buttonState.label}</span>
                      </Button>
                    );
                  })()}

                  {!practicalActions && onPreview && (
                    <Button
                      variant="link"
                      className="w-full sm:w-auto justify-start"
                      onClick={() => onPreview(lesson.id!)}
                      disabled={isLoading}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {/* B3 (spec §8): la provocari, profesorul vede „Detalii"; comportamentul ramane previzualizarea. */}
                      <span className="text-wrap">
                        {lesson.type === 'MONTHLY_CHALLENGE' ? 'Detalii' : 'Previzualizare'}
                      </span>
                    </Button>
                  )}

                  {/* Open PDF Exercises Button - Professor */}
                  {/* Task 15: descarcare reala prin backend; un PDF din afara containerului nostru se
                      deschide sincron in tab nou; un esec dupa cerere arata linkul „Deschide PDF-ul". */}
                  {hasPdfManual && lesson.id && (
                    <ButonDescarcaExercitii
                      lessonId={lesson.id}
                      titlu={lesson.title ?? ''}
                      pdfManualUrl={lesson.pdfManualUrl!}
                      className="w-full sm:w-auto"
                      disabled={isLoading}
                      eticheta={isClassExercise ? 'Descarca intrebarile' : undefined}
                    />
                  )}
                  {/* Fisa cu raspunsurile bifate (cheia temei online): doar prin backend. */}
                  {isClassExercise && lesson.areRaspunsuriPdf && lesson.id && (
                    <ButonDescarcaRaspunsuri
                      lessonId={lesson.id}
                      titlu={lesson.title ?? ''}
                      className="w-full sm:w-auto"
                      disabled={isLoading}
                    />
                  )}
                </>
              )}

              {/* Student Actions - Uses attemptState for button logic */}
              {userRole === Role.ELEV && canComplete && onComplete && !isHidden && (
                <>
                  {(() => {
                    const buttonState = getButtonState(attemptState);
                    return (
                      <Button
                        variant={buttonState.variant}
                        className={cn(
                          'w-full',
                          buttonState.variant === 'default' && 'bg-primary hover:bg-blue-700 text-white',
                          buttonState.disabled && 'opacity-50 cursor-not-allowed'
                        )}
                        onClick={() => onComplete(lesson.id!)}
                        disabled={isLoading || buttonState.disabled}
                        title={
                          buttonState.isPractice
                            ? 'Mod exersare - răspunsurile nu vor fi salvate'
                            : buttonState.disabled
                            ? 'Așteptați aprobarea profesorului înainte de a continua'
                            : undefined
                        }
                      >
                        {buttonState.icon}
                        <span className="text-wrap">{buttonState.label}</span>
                      </Button>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Hidden Badge */}
        {isHidden && isAdmin && (
          <Badge variant="secondary" className="lesson-badge-hidden">
            Ascuns
          </Badge>
        )}

        {/* Status Badges - Show based on attemptState */}
        {attemptState === 'PENDING_APPROVAL' && (
          <Badge variant="secondary" className="lesson-badge-pending">
            În Așteptare Aprobare
          </Badge>
        )}

        {attemptState === 'APPROVED' && (
          <Badge variant="secondary" className="lesson-badge-approved">
            Aprobat
          </Badge>
        )}

        {attemptState === 'REJECTED' && (
          <Badge variant="secondary" className="lesson-badge-rejected">
            Respins
          </Badge>
        )}

        {attemptState === 'IN_PROGRESS' && (
          <Badge variant="secondary" className="lesson-badge-in-progress">
            În Progres
          </Badge>
        )}

        {attemptState === 'COMPLETED' && (
          <Badge variant="secondary" className="lesson-badge-completed">
            Completat
          </Badge>
        )}

    </div>
  );
};

export default LessonCard;

